import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calculator, Menu, LogOut, User } from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";

const Header = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { user, loading, signOut } = useAuth();

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services" },
    { path: "/about", label: "About" },
    { path: "/faq", label: "FAQ & Pricing" },
    { path: "/new-client", label: "New Client" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 w-full border-b-2 border-secondary/20 bg-gradient-to-r from-card via-card to-muted/20 backdrop-blur shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 font-quicksand font-bold text-xl bg-gradient-to-r from-secondary via-primary to-accent bg-clip-text text-transparent hover:opacity-90 transition-all duration-300 drop-shadow-sm">
            <img 
              src="/images/logo-tiny-tax-lady.png" 
              alt="Tiny Tax Lady" 
              className="h-10 w-10 rounded-full object-cover ring-2 ring-secondary/30 ring-offset-2 shadow-lg"
            />
            <span>Tiny Tax Lady</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-semibold transition-all duration-300 relative after:content-[''] after:absolute after:w-full after:h-0.5 after:bottom-0 after:left-0 after:bg-gradient-to-r after:from-secondary after:to-accent after:scale-x-0 after:origin-left after:transition-transform after:duration-300 hover:after:scale-x-100 ${
                  isActive(link.path) ? "text-secondary after:scale-x-100" : "text-foreground/80 hover:text-secondary"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Button asChild size="sm" className="ml-2 shadow-md hover:shadow-lg transition-shadow">
              <Link to="/schedule-appointment">Book Appointment</Link>
            </Button>
            {!loading && (
              user ? (
                <>
                  <Button asChild variant="outline" size="sm" className="ml-2">
                    <Link to="/client-portal">
                      <User className="h-4 w-4 mr-2" />
                      Client Portal
                    </Link>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="ml-2"
                    onClick={signOut}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </Button>
                </>
              ) : (
                <Button asChild variant="outline" size="sm" className="ml-2">
                  <Link to="/auth/login">Login</Link>
                </Button>
              )
            )}
          </nav>

          {/* Mobile Navigation */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <nav className="flex flex-col gap-4 mt-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`text-lg font-semibold transition-all duration-300 relative after:content-[''] after:absolute after:w-full after:h-0.5 after:bottom-0 after:left-0 after:bg-gradient-to-r after:from-secondary after:to-accent after:scale-x-0 after:origin-left after:transition-transform after:duration-300 hover:after:scale-x-100 ${
                      isActive(link.path) ? "text-secondary after:scale-x-100" : "text-foreground hover:text-secondary"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Button asChild className="mt-4 shadow-md">
                  <Link to="/schedule-appointment" onClick={() => setIsOpen(false)}>
                    Book Appointment
                  </Link>
                </Button>
                {!loading && (
                  user ? (
                    <>
                      <Button asChild variant="outline" className="mt-2">
                        <Link to="/client-portal" onClick={() => setIsOpen(false)}>
                          <User className="h-4 w-4 mr-2" />
                          Client Portal
                        </Link>
                      </Button>
                      <Button 
                        variant="ghost" 
                        className="mt-2"
                        onClick={() => {
                          signOut();
                          setIsOpen(false);
                        }}
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Sign Out
                      </Button>
                    </>
                  ) : (
                    <Button asChild variant="outline" className="mt-2">
                      <Link to="/auth/login" onClick={() => setIsOpen(false)}>
                        Login
                      </Link>
                    </Button>
                  )
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
